import { useState } from "react";
import { Avatar, LiveBadge, VybePill, Ic, ICONS } from "../components/UI.jsx";
import { useSpacesStore } from "../context/store.js";

const B = {
  bg:"#08070F",bg2:"#0D0C18",bg3:"#131124",
  violet:"#7C3AED",violetLt:"#8B5CF6",
  violetDim:"rgba(124,58,237,0.15)",violetBorder:"rgba(124,58,237,0.3)",
  vibeGreen:"#10F5A0",vibeGreenDim:"rgba(16,245,160,0.12)",
  coral:"#FF6B6B",coralDim:"rgba(255,107,107,0.12)",coralBorder:"rgba(255,107,107,0.28)",
  amber:"#FFB830",sky:"#38BDF8",purple:"#A78BFA",teal:"#2DD4BF",
  text:"#F0EFFE",text2:"#7C7A9E",text3:"#3D3B5C",
  border:"rgba(124,58,237,0.15)",border2:"rgba(124,58,237,0.08)",
  grad:"linear-gradient(135deg,#7C3AED,#10F5A0)",gradCoral:"linear-gradient(135deg,#7C3AED,#FF6B6B)",
  font:"'Sora',system-ui,sans-serif",mono:"'Space Mono',monospace",
};

// Fallback data when API has no live spaces yet
const DEMO_SPACES = [
  { _id:"s1",title:"Building Your Second Brain in 2026",host:{displayName:"Leon Chen",initials:"LC",color:B.sky,verified:true},category:"Human Potential",status:"live",color:B.sky,listenerCount:1840 },
  { _id:"s2",title:"Web3 DAO Governance — Deep Vibe",host:{displayName:"Aisha Kamara",initials:"AK",color:B.vibeGreen,verified:true},category:"Tech Vibes",status:"live",color:B.vibeGreen,listenerCount:423 },
  { _id:"s3",title:"AI Art & Climate Data — Collector Preview",host:{displayName:"Jade Nakamura",initials:"JN",color:B.amber,verified:true},category:"Creative Learn",status:"scheduled",scheduledAt:new Date(Date.now()+7200000).toISOString(),color:B.amber,listenerCount:0 },
  { _id:"s4",title:"African AgriTech: Scale & Impact",host:{displayName:"Remi Kowalski",initials:"RK",color:B.purple},category:"Global Connect",status:"scheduled",scheduledAt:new Date(Date.now()+86400000).toISOString(),color:B.purple,listenerCount:0 },
];

const catColor = {"Tech Vibes":B.sky,"Global Connect":B.vibeGreen,"Creative Learn":B.amber,"Human Potential":B.purple,"AgriTech":B.teal,"DAO Governance":B.coral};
const fakeListeners = [{initials:"AK",color:B.vibeGreen},{initials:"MO",color:B.purple},{initials:"JN",color:B.amber},{initials:"RK",color:B.coral},{initials:"TP",color:B.purple}];

export default function SpacesPage({ onStream, onCall }) {
  const { spaces, loading } = useSpacesStore();
  const [filter, setFilter] = useState("all");
  const displaySpaces = spaces.length > 0 ? spaces : DEMO_SPACES;
  const filtered = filter === "all" ? displaySpaces : displaySpaces.filter(s => s.status === filter);
  const live = displaySpaces.filter(s => s.status === "live");
  const scheduled = displaySpaces.filter(s => s.status === "scheduled");

  const fmtScheduled = (iso) => {
    try {
      const d = new Date(iso);
      const diff = d - Date.now();
      if (diff < 3600000) return `In ${Math.round(diff/60000)}m`;
      if (diff < 86400000) return `Today ${d.toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"})}`;
      return `Tomorrow ${d.toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"})}`;
    } catch { return "Scheduled"; }
  };

  return (
    <div style={{ display:"flex",flexDirection:"column",height:"100%",overflow:"hidden" }}>
      {/* Header */}
      <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",padding:"14px 16px",borderBottom:`1px solid ${B.border2}`,flexShrink:0 }}>
        <div>
          <div style={{ fontWeight:900,fontSize:22,color:B.text }}>Spaces</div>
          <div style={{ fontSize:13,color:B.text2,marginTop:2 }}>Live audio rooms & scheduled sessions</div>
        </div>
        <button onClick={onStream} style={{
          display:"flex",alignItems:"center",gap:8,padding:"10px 20px",borderRadius:9999,
          border:"none",cursor:"pointer",fontFamily:B.font,fontWeight:700,fontSize:14,
          background:B.gradCoral,color:"#fff",boxShadow:`0 4px 20px rgba(255,107,107,0.3)`,
        }}>
          <Ic d={ICONS.stream} s={15} c="#fff" /> New Space
        </button>
      </div>

      {/* Filter tabs */}
      <div style={{ display:"flex",borderBottom:`1px solid ${B.border2}`,flexShrink:0 }}>
        {[["all","All"],["live","Live Now"],["scheduled","Scheduled"]].map(([k,l])=>(
          <button key={k} onClick={()=>setFilter(k)} style={{
            flex:1,padding:"12px 0",textAlign:"center",fontSize:14,
            fontWeight:filter===k?700:400,color:filter===k?B.text:B.text2,
            border:"none",background:"none",cursor:"pointer",fontFamily:B.font,
            borderBottom:filter===k?`3px solid ${B.violet}`:"3px solid transparent",
          }}
            onMouseEnter={e=>e.currentTarget.style.background=B.violetDim}
            onMouseLeave={e=>e.currentTarget.style.background="none"}>
            {l}
            {k==="live"&&live.length>0&&<span style={{ marginLeft:6,padding:"1px 7px",borderRadius:9999,background:B.coral,color:"#fff",fontSize:10,fontWeight:700,fontFamily:B.mono }}>{live.length}</span>}
          </button>
        ))}
      </div>

      {/* Spaces list */}
      <div style={{ flex:1,overflowY:"auto" }}>
        {loading && <div style={{ padding:40,textAlign:"center" }}><div className="vyl-spinner" style={{ margin:"0 auto" }} /></div>}

        {!loading && filtered.length === 0 && (
          <div style={{ padding:48,textAlign:"center" }}>
            <div style={{ fontSize:48,marginBottom:16 }}>🎙️</div>
            <div style={{ fontWeight:700,fontSize:20,color:B.text,marginBottom:8 }}>No spaces {filter !== "all" ? filter : ""} right now</div>
            <button onClick={onStream} style={{ padding:"10px 24px",borderRadius:9999,border:"none",background:B.grad,color:"#fff",fontFamily:B.font,fontWeight:700,fontSize:15,cursor:"pointer",boxShadow:"0 4px 20px rgba(124,58,237,0.4)" }}>
              Start the first Space ✦
            </button>
          </div>
        )}

        {filtered.map((space,i) => {
          const cc = space.color || catColor[space.category] || B.violet;
          const isLive = space.status === "live";
          const count = space.listenerCount ?? space.listeners?.length ?? 0;
          return (
            <div key={space._id||i} style={{
              display:"flex",gap:14,padding:"16px",borderBottom:`1px solid ${B.border2}`,
              cursor:"pointer",position:"relative",overflow:"hidden",transition:"background 0.15s",
            }}
              onMouseEnter={e=>e.currentTarget.style.background=B.violetDim}
              onMouseLeave={e=>e.currentTarget.style.background="none"}>
              {/* Top color line for live */}
              {isLive&&<div style={{ position:"absolute",top:0,left:0,right:0,height:2,background:cc,opacity:0.8 }} />}

              {/* Icon */}
              <div style={{ width:54,height:54,borderRadius:14,background:`${cc}15`,border:`1px solid ${cc}25`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:26,flexShrink:0,boxShadow:isLive?`0 0 16px ${cc}20`:"none" }}>
                {isLive ? "🎙️" : "📅"}
              </div>

              {/* Content */}
              <div style={{ flex:1,minWidth:0 }}>
                <div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:6,flexWrap:"wrap" }}>
                  {isLive ? <LiveBadge /> : <span style={{ fontSize:12,color:B.text2,fontFamily:B.mono }}>📅 {fmtScheduled(space.scheduledAt)}</span>}
                  <VybePill label={space.category||"General"} color={cc} />
                </div>
                <div style={{ fontWeight:700,fontSize:15,color:B.text,marginBottom:6,lineHeight:1.3 }}>{space.title}</div>
                <div style={{ display:"flex",alignItems:"center",gap:8 }}>
                  <Avatar user={space.host} size={20} />
                  <span style={{ fontSize:13,color:B.text2 }}>{space.host?.displayName||"Host"}</span>
                  {isLive&&<><span style={{ color:B.text3 }}>·</span><span style={{ fontSize:13,color:cc,fontFamily:B.mono }}>{count.toLocaleString()} listening</span></>}
                </div>
                {isLive && count > 0 && (
                  <div style={{ display:"flex",marginTop:10 }}>
                    {fakeListeners.map((u,j)=>(
                      <div key={j} style={{ marginLeft:j===0?0:-8,zIndex:5-j,width:24,height:24,borderRadius:"50%",background:`${u.color}25`,border:`1.5px solid ${B.bg}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:8,fontWeight:700,color:u.color,fontFamily:B.mono }}>
                        {u.initials}
                      </div>
                    ))}
                    {count > 5 && (
                      <div style={{ marginLeft:-8,width:24,height:24,borderRadius:"50%",background:B.violetDim,border:`1.5px solid ${B.bg}`,display:"flex",alignItems:"center",justifyContent:"center" }}>
                        <span style={{ color:B.text2,fontSize:9,fontFamily:B.mono }}>+{count-5}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Action button */}
              <div style={{ display:"flex",flexDirection:"column",alignItems:"flex-end",gap:6,flexShrink:0,justifyContent:"center" }}>
                {isLive ? (
                  <button onClick={()=>onCall?.({type:"stream",name:space.title,user:space.host})} style={{
                    padding:"9px 20px",borderRadius:9999,border:`1.5px solid ${cc}40`,
                    background:`${cc}12`,color:cc,cursor:"pointer",fontFamily:B.font,fontWeight:700,fontSize:14,
                    boxShadow:`0 0 14px ${cc}20`,transition:"all 0.2s",
                  }}
                    onMouseEnter={e=>{e.currentTarget.style.background=`${cc}25`;}}
                    onMouseLeave={e=>{e.currentTarget.style.background=`${cc}12`;}}>
                    Join
                  </button>
                ) : (
                  <button style={{ padding:"8px 16px",borderRadius:9999,border:`1px solid ${B.border}`,background:"none",color:B.text2,cursor:"pointer",fontFamily:B.font,fontSize:13 }}>
                    Remind me
                  </button>
                )}
              </div>
            </div>
          );
        })}

        {/* Create CTA */}
        <div style={{ margin:20,padding:24,borderRadius:20,background:`linear-gradient(135deg,rgba(124,58,237,0.1),rgba(16,245,160,0.06))`,border:`1px solid ${B.violetBorder}`,textAlign:"center" }}>
          <div style={{ fontSize:28,marginBottom:10 }}>🎙️</div>
          <div style={{ fontWeight:700,fontSize:18,color:B.text,marginBottom:6 }}>Start Your Own Space</div>
          <div style={{ color:B.text2,fontSize:14,marginBottom:16,lineHeight:1.6 }}>Host a live audio room for your community. Share knowledge, vibe together.</div>
          <button onClick={onStream} style={{ padding:"10px 28px",borderRadius:9999,border:"none",background:B.grad,color:"#fff",fontFamily:B.font,fontWeight:700,fontSize:15,cursor:"pointer",boxShadow:"0 4px 20px rgba(124,58,237,0.4)" }}>
            Start a Space ✦
          </button>
        </div>
      </div>
    </div>
  );
}
